import { useLayoutEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

interface AshlarLogoProps {
  className?: string;
}

/* Wordmark geometry, in user units where 1em = 100 (the keystone period and the
   gap before it are expressed in em so they scale with the type). */
const FS = 100;              // font size
const TRACK = -2;            // -0.02em letter-spacing
const STOP_H = 0.30 * FS;    // keystone "period" height (quiet)
const STOP_W = STOP_H * 0.62;
const GAP = 0.18 * FS;       // breathing room before the period (locked at .18)
const PAD = 2;               // hair of viewBox padding

// keystone kite mapped so its BOTTOM sits on the text baseline (y = 0, up = -)
const stopPoints = (x: number) => {
  const shoulderY = -STOP_H * (28 / 50); // where the kite is widest
  return [
    `${x + STOP_W / 2},${-STOP_H}`, // top
    `${x},${shoulderY}`,            // left
    `${x + STOP_W / 2},0`,          // bottom (on baseline)
    `${x + STOP_W},${shoulderY}`,   // right
  ].join(' ');
};

/**
 * Ashlar wordmark — "Ashlar App." with a lavender keystone full-stop.
 *
 * Weight contrast (Ashlar 800 / App 400), the period in the keystone lavender.
 * Theme-aware via brand tokens (--g5-accent / --g5-sub / --g5-key), so it tracks
 * light/dark. Rendered as an SVG so it scales by height like a logo
 * (`h-8 w-auto`, etc.); a layout-effect measures the live text to keep the
 * viewBox tight and the period's gap exact regardless of how Archivo renders.
 */
export function AshlarLogo({ className }: AshlarLogoProps) {
  const textRef = useRef<SVGTextElement>(null);
  // Fallbacks are corrected by the layout-effect before paint; they only matter
  // if measurement is unavailable (e.g. the element is hidden when first mounted).
  const [box, setBox] = useState('-2 -76 566 100');
  const [stopX, setStopX] = useState(520);

  useLayoutEffect(() => {
    const measure = () => {
      const t = textRef.current;
      if (!t) return;
      try {
        const len = t.getComputedTextLength();   // advance width, includes tracking
        const bb = t.getBBox();                   // tight glyph box (asc/descenders)
        const px = len + GAP;
        const left = Math.min(bb.x, px) - PAD;
        const top = Math.min(bb.y, -STOP_H) - PAD;
        const right = Math.max(bb.x + bb.width, px + STOP_W) + PAD;
        const bottom = Math.max(bb.y + bb.height, 0) + PAD;
        setStopX(px);
        setBox(`${left} ${top} ${right - left} ${bottom - top}`);
      } catch {
        /* element not measurable (hidden) — keep fallback */
      }
    };
    measure();
    // Re-measure once the brand font loads (first, uncached visit).
    const fonts = (document as unknown as { fonts?: FontFaceSet }).fonts;
    fonts?.ready?.then(measure).catch(() => {});
  }, []);

  return (
    <svg
      viewBox={box}
      className={cn('h-10 w-auto', className)}
      role="img"
      aria-label="Ashlar App"
      xmlns="http://www.w3.org/2000/svg"
      style={{ overflow: 'visible' }}
    >
      <text
        ref={textRef}
        x={0}
        y={0}
        fontFamily="'Archivo', system-ui, sans-serif"
        fontSize={FS}
        letterSpacing={TRACK}
        style={{ textRendering: 'geometricPrecision' }}
      >
        <tspan fontWeight={800} style={{ fill: 'var(--g5-accent)' }}>Ashlar</tspan>
        <tspan fontWeight={400} style={{ fill: 'var(--g5-sub)' }}> App</tspan>
      </text>
      <polygon points={stopPoints(stopX)} style={{ fill: 'var(--g5-key)' }} />
    </svg>
  );
}
